import styled from '@emotion/styled';
import { ChevronLeft, Copy, Image as ImageIcon, RotateCcw, Save } from 'lucide-react';
import { useFabricEditorOptional } from 'modules/fabric-editor';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { selectActiveTemplate } from 'modules/manager/store';
import { useResetTemplate, useSaveTemplate } from 'modules/manager/ui/hooks';
import useDuplicateTemplateModal from 'modules/manager/ui/hooks/modals/use-duplicate-template-modal';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsLocal from 'shared/hooks/use-is-local';
import { useAppSelector } from 'store/hooks';
import { editorAccent } from '../editor-accent';
import RenderPreviewModal from './render-preview-modal';
import { theme } from 'styles/theme/theme';

const EditorHeader: React.FC = () => {
  const navigate = useNavigate();
  const template = useAppSelector(selectActiveTemplate);
  const editor = useFabricEditorOptional();
  const { save, isDirty, isSaving, canSave, canEdit } = useSaveTemplate(editor);
  const { reset, canReset } = useResetTemplate(editor);
  const isLocal = useIsLocal();
  const [isRenderPreviewOpen, setRenderPreviewOpen] = useState(false);

  const handleBack = useCallback(() => {
    navigate(Pages.MEDIA_MANAGER.INDEX);
  }, [navigate]);

  // From the editor the user is already editing, so open the copy straight away
  // instead of bouncing through the list.
  const onDuplicated = useCallback((copy: MediaManagerTemplate) => navigate(Pages.MEDIA_MANAGER.builders.editor(copy.id)), [navigate]);

  const { modal: duplicateModal, open: openDuplicateModal, canDuplicate, isDuplicating } = useDuplicateTemplateModal({ onDuplicated });

  const canDevRender = isLocal && template != null;
  // A regular user opened a global preset: it is read-only until they duplicate it.
  const isReadOnlyPreset = template != null && !canEdit;

  return (
    <Root>
      <BackButton type="button" onClick={handleBack} aria-label="Назад">
        <ChevronLeft size={18} />
      </BackButton>
      <Title>
        <span className="font-semibold">{template?.name ?? '—'}</span>
        {isReadOnlyPreset ? (
          <ReadOnlyBadge>Пресет — дублируйте, чтобы редактировать</ReadOnlyBadge>
        ) : (
          isDirty && <UnsavedBadge>Несохранённые изменения</UnsavedBadge>
        )}
      </Title>
      <Spacer />
      {canDevRender && (
        <DevRenderButton
          type="button"
          onClick={() => setRenderPreviewOpen(true)}
          title="Только для локальной разработки. Отрисовать шаблон на бэкенде с тестовыми параметрами. Рендерится сохранённая версия шаблона — несохранённые изменения в картинку не попадут."
        >
          <ImageIcon size={16} />
          <span>Рендер</span>
        </DevRenderButton>
      )}
      {isReadOnlyPreset ? (
        <SaveButton
          type="button"
          onClick={() => template && openDuplicateModal(template)}
          disabled={!canDuplicate || isDuplicating}
          aria-label="Дублировать"
          title={canDuplicate ? undefined : 'Нет активного турнира для копии'}
        >
          <Copy size={16} />
          <span>{isDuplicating ? 'Копирование…' : 'Дублировать'}</span>
        </SaveButton>
      ) : (
        <>
          <ResetButton
            type="button"
            onClick={reset}
            disabled={!canReset || isSaving}
            aria-label="Сбросить изменения"
            title="Вернуть шаблон к последнему сохранённому состоянию. Сам сброс отменяется через Ctrl+Z."
          >
            <RotateCcw size={16} />
            <span>Сбросить</span>
          </ResetButton>
          <SaveButton type="button" onClick={() => void save()} disabled={!canSave} aria-label="Сохранить">
            <Save size={16} />
            <span>{isSaving ? 'Сохранение…' : 'Сохранить'}</span>
          </SaveButton>
        </>
      )}
      {duplicateModal}
      {isRenderPreviewOpen && template && <RenderPreviewModal template={template} onClose={() => setRenderPreviewOpen(false)} />}
    </Root>
  );
};

export default EditorHeader;

const Root = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 56px;
  border-bottom: 1px solid ${theme.colors.DarkBlue100};
  background: ${theme.colors.SurfaceWhite};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: ${theme.colors.DarkBlue400};
  &:hover {
    background: ${theme.colors.SurfaceCultured10};
  }
`;

const Title = styled.div`
  font-size: 14px;
  color: ${theme.colors.DarkBlue600};
  display: flex;
  align-items: center;
`;

const UnsavedBadge = styled.span`
  margin-left: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: ${theme.colors.DarkYellow100};
  /* warning читается только на тёмном; на этом чипе он даёт 1.88:1, нужен тёмный янтарь. */
  color: ${theme.colors.DarkYellow800};
  font-size: 12px;
  font-weight: 500;
`;

const ReadOnlyBadge = styled.span`
  margin-left: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: ${editorAccent.soft};
  color: ${editorAccent.ink};
  font-size: 12px;
  font-weight: 500;
`;

const Spacer = styled.div`
  flex: 1;
`;

/** Local-only — see dev-render-params.ts. Styled as a secondary action so it never competes with Save. */
const DevRenderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${theme.colors.DarkBlue200};
  color: ${theme.colors.DarkBlue400};
  font-size: 14px;
  font-weight: 500;
  &:hover {
    background: ${theme.colors.BorderGray50};
  }
`;

// Discarding edits is undoable (Ctrl+Z), so this stays a quiet outline button
// rather than a red destructive one — and it must not compete with Save.
const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${theme.colors.DarkBlue200};
  color: ${theme.colors.DarkBlue400};
  font-size: 14px;
  font-weight: 500;
  &:hover:not(:disabled) {
    background: ${theme.colors.BorderGray50};
  }
  &:disabled {
    color: ${theme.colors.DarkBlue200};
    border-color: ${theme.colors.DarkBlue100};
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  background: ${editorAccent.solid};
  color: ${editorAccent.onSolid};
  font-size: 14px;
  font-weight: 500;
  &:hover:not(:disabled) {
    background: ${editorAccent.solidHover};
  }
  &:disabled {
    background: ${theme.colors.DarkBlue100};
    color: ${theme.colors.DarkBlue300};
    cursor: not-allowed;
  }
`;
